import React, { Component } from "react";

import {
    MDBBtn,
    MDBRow,
    MDBCol,
    MDBIcon,
    MDBModal,
    MDBModalHeader,
    MDBModalBody,
    MDBModalFooter,
    toast,
} from "mdbreact";

import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import OutlinedInput from "@mui/material/OutlinedInput";

import JSONPretty from "react-json-pretty";

import { withTranslation } from "react-i18next";
import { DateInput } from "semantic-ui-calendar-react";

import axios from "../../../axios";

import {
    getFunctionNameLabel,
} from "../../../components/NetworkMgmt/Functions/logFunctionNames";
import { getEventGroupsForScope } from "../../../components/NetworkMgmt/Functions/logEventGroups";

function QuickSearchToolbar() {
    return (
        <Box sx={{ p: 0.5, pb: 0 }}>
            <GridToolbarQuickFilter />
        </Box>
    );
}

// Number of log entries requested per page from the API.
const PAGE_SIZE = 10;

// Shared log viewer that talks to the unified /operatorconsole/listLogs
// endpoint. Restricts scope based on props (logType + identifier), supports
// filtering by function_name and by min/max date, and supports pagination via
// the API's searchBefore cursor.
//
// Props:
//   logType: "operator" | "venue" | "subscriber" | "unit" | "equipment"
//   venueID / subscriberToken / unitIDInternal / equipmentID: scope identifiers
class LogViewer extends Component {
    constructor(props) {
        super(props);
        // Wraps the Events label + dropdown so outside clicks can be detected.
        this.eventsWrapperRef = React.createRef();
        this.state = {
            logs: [],
            loading: false,
            // Raw function_name values selected for the query (built from Event
            // Groups and/or individual sub-events).
            selectedFunctions: [],
            // Controls the Events multi-select menu so Apply/Clear can close it.
            eventsMenuOpen: false,
            // Which Event Group categories are expanded to show their sub-events.
            expandedGroups: {},
            dateStart: "",
            dateEnd: "",
            // Pagination: stack of searchBefore cursors for previous pages, and
            // the cursor to fetch the next page.
            searchBeforeStack: [],
            nextSearchBefore: undefined,
            pageIndex: 0,
            modalDetail: false,
            detailLog: undefined,
        };
    }

    componentDidMount() {
        this.getLogs();
        document.addEventListener("mousedown", this.handleClickOutsideEvents);
    }

    componentWillUnmount() {
        document.removeEventListener("mousedown", this.handleClickOutsideEvents);
    }

    // Closes the Events dropdown when clicking anywhere outside of it.
    handleClickOutsideEvents = (e) => {
        if (
            this.state.eventsMenuOpen &&
            this.eventsWrapperRef.current &&
            !this.eventsWrapperRef.current.contains(e.target)
        ) {
            this.ignoreOpenUntil = Date.now() + 300;
            this.setState({ eventsMenuOpen: false });
        }
    };

    componentDidUpdate(prevProps) {
        const scopeChanged =
            prevProps.venueID !== this.props.venueID ||
            prevProps.subscriberToken !== this.props.subscriberToken ||
            prevProps.unitIDInternal !== this.props.unitIDInternal ||
            prevProps.equipmentID !== this.props.equipmentID;
        if (scopeChanged) {
            this.setState(
                {
                    logs: [],
                    selectedFunctions: [],
                    expandedGroups: {},
                    dateStart: "",
                    dateEnd: "",
                    searchBeforeStack: [],
                    nextSearchBefore: undefined,
                    pageIndex: 0,
                },
                () => this.getLogs()
            );
        }
    }

    // Builds the scope-specific portion of the request body.
    getScopeBody = () => {
        switch (this.props.logType) {
            case "venue":
                return { VenueID: this.props.venueID };
            case "subscriber":
                return { SubscriberToken: this.props.subscriberToken };
            case "unit":
                return { UnitID_internal: this.props.unitIDInternal };
            case "equipment":
                return { EquipmentID: this.props.equipmentID };
            case "operator":
            default:
                return {};
        }
    };

    // Fetches every page of logs for the current scope (following the
    // searchBefore cursor) and stores the distinct function_name values, so the
    // filter dropdown reflects all functions, not just the most recent page.
    getLogs = (searchBefore) => {
        this.setState({ loading: true });

        const body = {
            AccessToken: sessionStorage.getItem("AccessToken"),
            IdToken: sessionStorage.getItem("IdToken"),
            pageSize: PAGE_SIZE,
            ...this.getScopeBody(),
        };

        if (this.state.selectedFunctions.length > 0) {
            body.function_name = this.state.selectedFunctions;
        }
        if (this.state.dateStart) {
            body["MinViewWindowDateUTC.ISO8601"] = new Date(
                this.state.dateStart
            ).toISOString();
        }
        if (this.state.dateEnd) {
            body["MaxViewWindowDateUTC.ISO8601"] = new Date(
                this.state.dateEnd
            ).toISOString();
        }
        if (searchBefore !== undefined) {
            body.searchBefore = searchBefore;
        }

        axios
            .post("/operatorconsole/listLogs", body, { headers: {} })
            .then((response) => {
                this.setState({
                    logs: response.data.log || [],
                    nextSearchBefore: response.data.searchBefore,
                    loading: false,
                });
            })
            .catch((error) => {
                toast.error(
                    error?.response?.data?.message ||
                        "There was an error loading logs!",
                    { closeButton: false }
                );
                this.setState({ loading: false });
            });
    };

    // Applies the current filter selections and resets pagination.
    applyFilters = () => {
        this.setState(
            {
                eventsMenuOpen: false,
                searchBeforeStack: [],
                pageIndex: 0,
            },
            () => this.getLogs()
        );
    };

    clearFilters = () => {
        this.setState(
            {
                selectedFunctions: [],
                expandedGroups: {},
                eventsMenuOpen: false,
                dateStart: "",
                dateEnd: "",
                searchBeforeStack: [],
                pageIndex: 0,
            },
            () => this.getLogs()
        );
    };

    // Whether every function in a group is currently selected.
    isGroupFullySelected = (group) =>
        group.functions.length > 0 &&
        group.functions.every((fn) =>
            this.state.selectedFunctions.includes(fn)
        );

    // Whether some (but not all) functions in a group are selected.
    isGroupPartiallySelected = (group) =>
        group.functions.some((fn) =>
            this.state.selectedFunctions.includes(fn)
        ) && !this.isGroupFullySelected(group);

    // Toggling a group selects or clears all of its nested sub-events.
    toggleGroup = (group) => {
        this.setState((prev) => {
            const set = new Set(prev.selectedFunctions);
            const allSelected = group.functions.every((fn) => set.has(fn));
            group.functions.forEach((fn) =>
                allSelected ? set.delete(fn) : set.add(fn)
            );
            return { selectedFunctions: Array.from(set) };
        });
    };

    // Toggling a single sub-event adds/removes it from the query array.
    toggleFunction = (fn) => {
        this.setState((prev) => {
            const set = new Set(prev.selectedFunctions);
            set.has(fn) ? set.delete(fn) : set.add(fn);
            return { selectedFunctions: Array.from(set) };
        });
    };

    // Expands or collapses an Event Group's nested sub-events.
    toggleGroupExpanded = (groupName) => {
        this.setState((prev) => ({
            expandedGroups: {
                ...prev.expandedGroups,
                [groupName]: !prev.expandedGroups[groupName],
            },
        }));
    };

    // Event Groups filtered to the current scope (logType).
    getScopedGroups = () => getEventGroupsForScope(this.props.logType);

    // Handles individual sub-event toggles from the MUI multi-select. Group
    // headers are handled via their own onClick (toggleGroup).
    handleEventsChange = (event) => {
        const value = event.target.value;
        this.setState({
            selectedFunctions:
                typeof value === "string" ? value.split(",") : value,
        });
    };

    // Summary text shown in the collapsed multi-select: fully-selected group
    // names, plus any individually-selected sub-events.
    renderEventsValue = (selected) => {
        if (!selected || selected.length === 0) return "";
        const fullGroups = this.getScopedGroups().filter((g) =>
            this.isGroupFullySelected(g)
        );
        const coveredByGroups = new Set(
            fullGroups.reduce((acc, g) => acc.concat(g.functions), [])
        );
        const individual = selected
            .filter((fn) => !coveredByGroups.has(fn))
            .map((fn) => getFunctionNameLabel(this.cleanFunctionName(fn)));
        return [...fullGroups.map((g) => g.name), ...individual].join(", ");
    };

    changeHandler = (event, data) => {
        if (data && this.state.hasOwnProperty(data.name)) {
            this.setState({ [data.name]: data.value });
            return;
        }

        this.setState({ [event.target.name]: event.target.value });
    };

    nextPage = () => {
        if (this.state.nextSearchBefore === undefined) return;
        const cursor = this.state.nextSearchBefore;
        this.setState(
            (prev) => ({
                searchBeforeStack: [...prev.searchBeforeStack, cursor],
                pageIndex: prev.pageIndex + 1,
            }),
            () => this.getLogs(cursor)
        );
    };

    prevPage = () => {
        if (this.state.pageIndex === 0) return;
        this.setState(
            (prev) => {
                const stack = [...prev.searchBeforeStack];
                stack.pop(); // remove current page cursor
                return {
                    searchBeforeStack: stack,
                    pageIndex: prev.pageIndex - 1,
                };
            },
            () => {
                // The cursor for the (now current) page is the last remaining
                // one on the stack, or undefined for the first page.
                const stack = this.state.searchBeforeStack;
                const cursor =
                    stack.length > 0 ? stack[stack.length - 1] : undefined;
                this.getLogs(cursor);
            }
        );
    };

    toggleDetail = (log) => {
        this.setState({
            modalDetail: !this.state.modalDetail,
            detailLog: log,
        });
    };

    // Strips API path prefixes (anything before a "/") and known service tokens
    // ("vault" and any "*console" segment, e.g. operatorconsole,
    // networkoperatorconsole, venuemgmtconsole, subscriberconsole) — whether
    // "/"- or "_"-delimited — from a raw function_name for display only. The raw
    // value is kept for API filtering.
    cleanFunctionName = (fn) => {
        if (!fn) return "";
        const isServiceToken = (part) => {
            const lower = part.toLowerCase();
            return lower === "vault" || lower.endsWith("console");
        };
        return String(fn)
            .split("/")
            .pop()
            .split("_")
            .filter((part) => part && !isServiceToken(part))
            .join("_")
            .replace(/^[_-]+|[_-]+$/g, "")
            .trim();
    };

    buildRows = () => {
        return (this.state.logs || []).map((log, index) => {
            const dateRaw = log["@timestamp"];
            let dateFormatted = "";
            if (dateRaw) {
                try {
                    dateFormatted = Intl.DateTimeFormat("en-US", {
                        year: "numeric",
                        month: "numeric",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                    }).format(new Date(dateRaw));
                } catch (e) {
                    dateFormatted = dateRaw;
                }
            }
            const eventValue = log?.event;
            let description = "";
            if (eventValue !== undefined && eventValue !== null) {
                description =
                    typeof eventValue === "object"
                        ? JSON.stringify(eventValue)
                        : String(eventValue);
            }
            return {
                id: index,
                date: dateFormatted,
                functionName: getFunctionNameLabel(
                    this.cleanFunctionName(log.function_name)
                ),
                description: description,
                administrator: log.Administrator || "",
                sourceIP: log.sourceIP || "",
                raw: log,
            };
        });
    };

    render() {
        const rows = this.buildRows();

        const columns = [
            { headerName: "Date", field: "date", flex: 0.2, minWidth: 160 },
            {
                headerName: "Function",
                field: "functionName",
                flex: 0.2,
                minWidth: 180,
            },
            {
                headerName: "Description",
                field: "description",
                flex: 0.4,
                minWidth: 240,
            },
            {
                headerName: "Administrator",
                field: "administrator",
                flex: 0.2,
                minWidth: 160,
            },
        ];

        return (
            <div className="logList">
                {/* Filters */}
                <MDBRow className="mb-2">
                    <MDBCol sm="12" md="4">
                        <div ref={this.eventsWrapperRef}>
                        <label className="black-text">Events</label>
                        <FormControl size="small" fullWidth>
                            <Select
                                multiple
                                displayEmpty
                                open={this.state.eventsMenuOpen}
                                onOpen={() => {
                                    // Clicking the select while it's open fires
                                    // onOpen again (MUI quirk), so toggle rather
                                    // than force-open. Guard against a paired
                                    // onClose firing in the same interaction.
                                    if (Date.now() < (this.ignoreOpenUntil || 0))
                                        return;
                                    this.setState((prev) => ({
                                        eventsMenuOpen: !prev.eventsMenuOpen,
                                    }));
                                }}
                                onClose={() => {
                                    this.ignoreOpenUntil = Date.now() + 300;
                                    this.setState({ eventsMenuOpen: false });
                                }}
                                value={this.state.selectedFunctions}
                                onChange={this.handleEventsChange}
                                input={<OutlinedInput />}
                                sx={{
                                    backgroundColor: "#fff",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: "1px solid #ced4da !important",
                                    },
                                }}
                                renderValue={(selected) =>
                                    selected && selected.length
                                        ? this.renderEventsValue(selected)
                                        : "All Events"
                                }
                                MenuProps={{
                                    transitionDuration: 0,
                                    disableScrollLock: true,
                                    disablePortal: true,
                                    TransitionProps: { timeout: 0 },
                                    PaperProps: { style: { maxHeight: 360 } },
                                }}
                            >
                                {this.getScopedGroups().reduce((items, group) => {
                                    const expanded =
                                        !!this.state.expandedGroups[group.name];
                                    items.push(
                                        <ListSubheader
                                            key={`group-${group.name}`}
                                            disableSticky
                                            onMouseDown={(e) => {
                                                // onMouseDown fires before MUI's
                                                // menu click handling swallows it.
                                                e.preventDefault();
                                                e.stopPropagation();
                                                this.toggleGroup(group);
                                            }}
                                            style={{
                                                cursor: "pointer",
                                                lineHeight: "36px",
                                            }}
                                        >
                                            <MDBIcon
                                                icon={
                                                    expanded
                                                        ? "caret-down"
                                                        : "caret-right"
                                                }
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    this.toggleGroupExpanded(
                                                        group.name
                                                    );
                                                }}
                                                className="mr-2 text-muted"
                                                style={{
                                                    cursor: "pointer",
                                                    width: "12px",
                                                }}
                                            />
                                            <Checkbox
                                                size="small"
                                                style={{ padding: 4 }}
                                                tabIndex={-1}
                                                disableRipple
                                                checked={this.isGroupFullySelected(
                                                    group
                                                )}
                                                indeterminate={this.isGroupPartiallySelected(
                                                    group
                                                )}
                                            />
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    color: "#000",
                                                }}
                                            >
                                                {group.name}
                                            </span>
                                        </ListSubheader>
                                    );
                                    if (expanded) {
                                        group.functions.forEach((fn) => {
                                            items.push(
                                                <MenuItem
                                                    key={fn}
                                                    value={fn}
                                                    dense
                                                    sx={{ pl: 4 }}
                                                >
                                                    <Checkbox
                                                        size="small"
                                                        checked={this.state.selectedFunctions.includes(
                                                            fn
                                                        )}
                                                    />
                                                    <ListItemText
                                                        primary={getFunctionNameLabel(
                                                            this.cleanFunctionName(
                                                                fn
                                                            )
                                                        )}
                                                    />
                                                </MenuItem>
                                            );
                                        });
                                    }
                                    return items;
                                }, [])}
                            </Select>
                        </FormControl>
                        </div>
                    </MDBCol>
                    <MDBCol sm="12" md="3">
                        <label className="black-text">From Date</label>
                        <DateInput
                            dateFormat="MM/DD/YYYY"
                            name="dateStart"
                            placeholder="MM/DD/YYYY"
                            value={this.state.dateStart}
                            iconPosition="left"
                            onChange={this.changeHandler}
                            className="dateTimePicker"
                            closable={true}
                        />
                    </MDBCol>
                    <MDBCol sm="12" md="3">
                        <label className="black-text">To Date</label>
                        <DateInput
                            dateFormat="MM/DD/YYYY"
                            name="dateEnd"
                            placeholder="MM/DD/YYYY"
                            value={this.state.dateEnd}
                            iconPosition="left"
                            onChange={this.changeHandler}
                            className="dateTimePicker"
                            minDate={this.state.dateStart}
                            initialDate={this.state.dateStart}
                            closable={true}
                        />
                    </MDBCol>
                    <MDBCol
                        sm="12"
                        md="2"
                        className="d-flex align-items-end"
                    >
                        <MDBBtn
                            size="sm"
                            color="danger"
                            onClick={this.applyFilters}
                            disabled={this.state.loading}
                        >
                            <MDBIcon icon="filter" className="mr-1" />
                            Apply
                        </MDBBtn>
                        <MDBBtn
                            size="sm"
                            color="primary"
                            onClick={this.clearFilters}
                            disabled={this.state.loading}
                        >
                            Clear
                        </MDBBtn>
                    </MDBCol>
                </MDBRow>

                {/* Log table */}
                <div style={{ width: "100%" }}>
                    <DataGrid
                        autoHeight
                        rows={rows}
                        columns={columns}
                        loading={this.state.loading}
                        hideFooterPagination={true}
                        hideFooter={true}
                        disableRowSelectionOnClick={true}
                        onRowClick={(params) => this.toggleDetail(params.row.raw)}
                        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
                        slots={{ toolbar: QuickSearchToolbar }}
                        localeText={{ noRowsLabel: "No Logs Found" }}
                    />
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-2">
                    <MDBBtn
                        size="sm"
                        color="primary"
                        onClick={this.prevPage}
                        disabled={this.state.pageIndex === 0 || this.state.loading}
                    >
                        <MDBIcon icon="chevron-left" className="mr-1" />
                        Previous
                    </MDBBtn>
                    <span className="text-muted">
                        Page {this.state.pageIndex + 1}
                    </span>
                    <MDBBtn
                        size="sm"
                        color="primary"
                        onClick={this.nextPage}
                        disabled={
                            this.state.nextSearchBefore === undefined ||
                            this.state.loading ||
                            rows.length === 0
                        }
                    >
                        Next
                        <MDBIcon icon="chevron-right" className="ml-1" />
                    </MDBBtn>
                </div>

                {/* Detail modal (individual JSON) */}
                <MDBModal
                    isOpen={this.state.modalDetail}
                    toggle={() => this.toggleDetail(undefined)}
                    size="lg"
                >
                    <MDBModalHeader toggle={() => this.toggleDetail(undefined)}>
                        Log Detail
                    </MDBModalHeader>
                    <MDBModalBody>
                        {this.state.detailLog !== undefined ? (
                            <>
                                <div className="mb-2">
                                    <MDBBtn
                                        size="sm"
                                        color="primary"
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                JSON.stringify(
                                                    this.state.detailLog,
                                                    null,
                                                    2
                                                )
                                            );
                                            toast.success(
                                                "JSON copied to clipboard!",
                                                { closeButton: false }
                                            );
                                        }}
                                    >
                                        <MDBIcon icon="copy" className="mr-1" />
                                        Copy JSON
                                    </MDBBtn>
                                </div>
                                <JSONPretty
                                    id="log-detail-json"
                                    data={this.state.detailLog}
                                    theme={{
                                        main: "line-height:1.3;color:#66d9ef;overflow:auto;",
                                        error: "line-height:1.3;color:#66d9ef;overflow:auto;",
                                        key: "color:#f92672;",
                                        string: "color:#a6e22e;",
                                        value: "color:#ae81ff;",
                                        boolean: "color:#ac81fe;",
                                    }}
                                    style={{
                                        whiteSpace: "pre",
                                        fontSize: "12px",
                                        fontFamily:
                                            'Monaco, Menlo, "Ubuntu Mono", monospace',
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        padding: "15px",
                                        backgroundColor: "#f8f9fa",
                                    }}
                                    space={2}
                                />
                            </>
                        ) : null}
                    </MDBModalBody>
                    <MDBModalFooter>
                        <MDBBtn
                            color="primary"
                            onClick={() => this.toggleDetail(undefined)}
                        >
                            Close
                        </MDBBtn>
                    </MDBModalFooter>
                </MDBModal>
            </div>
        );
    }
}

export default withTranslation()(LogViewer);
